import React from "react";
import styled from "styled-components";
import {
  FontSize,
  FontWeight,
  GlossyThemeProvider,
  Spacing,
  content,
} from "@glossy";
import Anchor from "@ui-kit/anchor";
import SafeHTML from "@ui-kit/safe-html";
import Markdown from "@radarly/components/cards/markdown";
import Meltmoji from "@radarly/components/insight-pages/discovery/meltmoji";
import { AlgoFieldId } from "@radarly/models/insight-pages/model";

export const TYPES = ["h1", "h2", "text"] as const;
type Type = typeof TYPES[number];

export interface IProps {
  content: string;
  isFirst?: boolean;
  type?: Type;
  asHTML?: boolean;
  icon?: AlgoFieldId;
  noAnchor?: boolean;
}

const Root = styled.div<{ isFirst: boolean; type: Type }>`
  font-weight: ${(props) => props.type !== "text" && FontWeight.Bold};
  white-space: ${(props) => props.type === "text" && "pre-wrap"};
  font-size: ${(props) =>
    props.type === "text" ? FontSize.Medium : FontSize.Large};
  color: ${(props) =>
    content(props, props.type === "h2" ? "secondary" : "primary")};
`;

const Flex = styled.div`
  display: flex;
  gap: ${Spacing.Medium};
  align-items: flex-start;
`;

const ConditionalWrapper = ({ condition, wrapper, children }) =>
  condition ? wrapper(children) : children;

const RadarlyCardsText = ({
  content,
  isFirst,
  type = "h1",
  asHTML,
  icon,
  noAnchor = false,
}: IProps) => (
  <GlossyThemeProvider name="body">
    <Root isFirst={isFirst} type={type}>
      <ConditionalWrapper
        condition={!noAnchor}
        wrapper={(children) => <Anchor id={content}>{children}</Anchor>}
      >
        <Flex>
          {icon && type === "h1" && <Meltmoji algoFieldId={icon} />}
          {type === "text" ? (
            <Markdown md={content} />
          ) : asHTML ? (
            <>
              <SafeHTML html={content} options={{ ALLOWED_TAGS: ["span"] }} />
            </>
          ) : (
            content
          )}
        </Flex>
      </ConditionalWrapper>
    </Root>
  </GlossyThemeProvider>
);

export default RadarlyCardsText;
